<script setup lang="ts">
    import { FormInputProps, FormInputData } from '~~/composables/useFormInput';

    const emit = defineEmits<{
        (e: 'blur', value: number | number): void;
        (e: 'change', value: number | number): void;
    }>();

    const props = withDefaults(
        defineProps<
            FormInputProps & {
                min?: number;
                max?: number;
                step?: number;
                mask: Function;
                unmask: Function;
                setter?: Function;
                filledByAi?: boolean;
            }
        >(),
        {
            min: 0,
            max: 1000000,
            step: 0.01,
        }
    );
    const inputEl = ref();
    const inputData: FormInputData = useFormInput(props);
    const focused: Ref<boolean> = ref(false);
    const internalModel = ref<string | number>(0);
    // What the field showed when it was entered, to tell an edit from a mere visit.
    let valueAtFocus: string | number | null = null;

    // Writes what is currently typed into the form value. Returns false and leaves the
    // model alone when the field holds something unparseable, so a half-typed entry
    // never lands as NaN.
    const commit = (): boolean => {
        const next = props.setter ? props.setter(internalModel.value) : parseFloat(internalModel.value as string);
        if (typeof next === 'number' && Number.isNaN(next)) return false;
        inputData.model.value = next;
        return true;
    };

    const focusOut = async () => {
        focused.value = false;
        // Wait for the input to be `type="text"` again before touching the model. The
        // watch below re-masks on any model change, and a masked string ("250,00 €")
        // assigned while the input is still `type="number"` is rejected by the browser,
        // which blanks the field.
        await nextTick();
        // Then commit. The form value is otherwise only written by the native `change`
        // event, and that event does not always arrive: dismissing the keyboard with
        // its own close key blurs the field without committing it. `mask()` below would
        // then rebuild the display from the STALE model and silently discard what the
        // user just typed.
        //
        // Only when something was typed, though. Committing on every blur round-tripped
        // an untouched value through `unmask` and the `setter`, which is not lossless:
        // a price is kept to a tenth of a cent there, so tabbing through a line computed
        // by the API (a deposit deduction, say) rewrote its amount. Main only wrote on
        // `change`, i.e. after an edit.
        if (String(internalModel.value) !== String(valueAtFocus)) commit();
        internalModel.value = props.mask(inputData.model.value);
        emit('blur', inputData.model.value);
    };

    const focusIn = async () => {
        focused.value = true;
        await nextTick();
        internalModel.value = props.unmask(inputData.model.value);
        valueAtFocus = internalModel.value;

        // After a second tick: the first one flips the input to `type="number"`, this
        // one lets the unmasked value reach the DOM. Selecting before that would
        // select the masked string that is about to be replaced.
        if (!props.selectOnFocus) return;
        await nextTick();
        inputEl.value?.select();
    };

    const onChange = () => {
        if (props.setter) {
            inputData.model.value = props.setter(internalModel.value);
            emit('change', inputData.model.value);

            return;
        }
        inputData.model.value = parseFloat(internalModel.value);
        emit('change', inputData.model.value);
    };

    const focus = () => {
        inputEl.value?.focus();
    };

    defineExpose({
        focus,
    });

    // Watch the inputData.model value and update the input value manually
    // This is needed because the FormattedNumberInput is not using it directly.
    watch(inputData.model, async (value) => {
        if (focused.value) {
            return;
        }
        // Wait a tick before writing the masked string. On the normal path the browser
        // fires `change` and `blur` back to back in one task: `onChange` updates the
        // model, `focusOut` clears `focused`, and this watcher then runs while the
        // input is STILL `type="number"`. A masked string ("60,00 €") assigned to a
        // number input is rejected outright and the field renders blank — the value was
        // saved, but the user sees an empty box. One tick lets the type flip to `text`
        // first.
        await nextTick();
        internalModel.value = props.mask(value);
    });

    onMounted(async () => {
        const value = inputData.model?.value || 0;
        internalModel.value = props.mask(value);
        if (props.autofocus && inputEl.value) {
            await wait(250);
            inputEl.value?.focus();
        }
    });
</script>

<template>
    <FormElement :input-data="inputData">
        <input
            :id="inputData.inputId.value"
            ref="inputEl"
            v-model="internalModel"
            :name="inputData.props.inputName"
            :placeholder="inputData.placeholder.value"
            :min="min"
            :max="max"
            :step="step"
            :type="focused ? 'number' : 'text'"
            inputmode="decimal"
            :disabled="inputData.props.disabled"
            :class="[filledByAi ? $theme('form.filled_ai') : inputData.className.value]"
            role="presentation"
            autocomplete="off"
            @focus="focusIn"
            @blur="focusOut"
            @change="onChange"
        />
    </FormElement>
</template>
